
import { mapActions, mapGetters } from 'vuex'
import firebase from '@/firebase'
import DeletedModels from '@/components/DeletedModels.vue'

// How often to poll the recalculating flag for an in-flight batch, and how long to wait for a
// single batch before giving up on it so the run can't stall forever.
const POLL_INTERVAL_MS = 12000
const BATCH_TIMEOUT_MS = 30 * 60 * 1000

export default {
  components: {
    DeletedModels
  },
  data () {
    return {
      afterDate: null,
      beforeDate: null,
      afterDateMenu: false,
      beforeDateMenu: false,
      selectedTypes: ['mmm', 'automatic', 'ekrm', 'reference'],
      batchSize: 3,
      maxModels: 0,
      previewing: false,
      previewDone: false,
      matches: [],
      undetermined: [],
      selectedMatches: [],
      confirmDialog: false,
      recalculating: false,
      progressDone: 0,
      progressTotal: 0,
      snack: false,
      snackColor: 'success',
      snackText: '',
      ngtorchBranch: null,
      ngtorchCommit: '',
      ngtorchReinstall: false,
      ngtorchBranches: [],
      ngtorchRefsTruncated: false,
      ngtorchLoadingRefs: false,
      ngtorchApplying: false,
      ngtorchClearing: false
    }
  },
  mounted () {
    if (this.isDevEnvironment) {
      this.fetchNgtorchRefs()
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser',
      'ngtorchOverride'
    ]),
    // Cosmetic gate only: every server-side path re-checks the project on its own, so hiding the
    // card is about not offering something that would be refused, not about enforcing anything.
    // Read off the app the page is actually connected to, not firebase-config.js: a deploy swaps
    // that file under a running dev server, and a hot reload would otherwise leave this view on
    // another project's id while the banner, fed from the dev user document, stays up.
    isDevEnvironment () {
      return firebase.db.app.options.projectId === 'ngapp5-dev'
    },
    ngtorchActive () {
      return Boolean(this.ngtorchOverride) && this.ngtorchOverride.enabled === true
    },
    // Which of the two the override is, and so whether it moves. The backend decides it when the
    // override is set - a pasted commit id is a prefix of the sha it resolves to, a branch name is
    // not - and re-reads the branch's head as each run is created.
    ngtorchFollowingBranch () {
      return this.ngtorchActive && this.ngtorchOverride.kind !== 'commit'
    },
    ngtorchBranchNames () {
      return this.ngtorchBranches.map(function (branch) { return branch.name })
    },
    // A pasted commit wins over a picked branch only because selecting either one clears the other.
    ngtorchSelectedRef () {
      var commit = this.ngtorchCommit ? this.ngtorchCommit.trim() : ''
      if (commit) return commit
      return this.ngtorchBranch || ''
    },
    typeOptions () {
      return [
        { value: 'mmm', text: this.typeLabelForKey('mmm') },
        { value: 'automatic', text: this.typeLabelForKey('automatic') },
        { value: 'ekrm', text: this.typeLabelForKey('ekrm') },
        { value: 'reference', text: this.typeLabelForKey('reference') }
      ]
    },
    matchHeaders () {
      return [
        { text: this.$t('views.adminTools.headerName'), value: 'name' },
        { text: this.$t('views.adminTools.headerType'), value: 'type' },
        { text: this.$t('views.adminTools.headerDate'), value: 'datetime' },
        { text: this.$t('views.adminTools.headerQuality'), value: 'quality' }
      ]
    },
    // The ids that will actually be triggered, after applying the optional per-run cap.
    effectiveSelectedIds () {
      const ids = this.selectedMatches.map(m => m.id)
      if (this.maxModels && this.maxModels > 0) {
        return ids.slice(0, this.maxModels)
      }
      return ids
    },
    effectiveSelectedCount () {
      return this.effectiveSelectedIds.length
    },
    progressPercent () {
      return this.progressTotal > 0 ? Math.round((this.progressDone / this.progressTotal) * 100) : 0
    }
  },
  methods: {
    ...mapActions('admin', [
      'previewBulkRecalculateModelReports',
      'bulkRecalculateModelReports',
      'getRecalculatingStatus',
      'loadNgtorchRefs',
      'setNgtorchOverride',
      'clearNgtorchOverride'
    ]),
    async fetchNgtorchRefs () {
      this.ngtorchLoadingRefs = true
      try {
        const result = await this.loadNgtorchRefs()
        this.ngtorchBranches = result.branches || []
        this.ngtorchRefsTruncated = result.truncated === true
      } catch (error) {
        console.error('Could not load ngtorch branches', error)
        this.showSnack(this.$t('views.adminTools.ngtorchLoadRefsError'), 'error')
      } finally {
        this.ngtorchLoadingRefs = false
      }
    },
    async applyNgtorchOverride () {
      const ref = this.ngtorchSelectedRef
      if (!ref) return
      this.ngtorchApplying = true
      try {
        const applied = await this.setNgtorchOverride({ ref: ref, reinstall: this.ngtorchReinstall })
        this.showSnack(this.$t('views.adminTools.ngtorchApplied', { ref: applied.ref, sha: applied.short_sha }), 'success')
      } catch (error) {
        console.error('Could not set the ngtorch override', error)
        // A ref GitHub does not know is the common mistake, and it is worth naming precisely so the
        // admin looks for a typo rather than for a permissions problem.
        const message = error && error.code === 'functions/not-found'
          ? this.$t('views.adminTools.ngtorchNotFound', { ref: ref })
          : this.$t('views.adminTools.ngtorchApplyError')
        this.showSnack(message, 'error')
      } finally {
        this.ngtorchApplying = false
      }
    },
    async disableNgtorchOverride () {
      this.ngtorchClearing = true
      try {
        await this.clearNgtorchOverride()
        this.ngtorchBranch = null
        this.ngtorchCommit = ''
        this.ngtorchReinstall = false
        this.showSnack(this.$t('views.adminTools.ngtorchDisabled'), 'success')
      } catch (error) {
        console.error('Could not disable the ngtorch override', error)
        this.showSnack(this.$t('views.adminTools.ngtorchApplyError'), 'error')
      } finally {
        this.ngtorchClearing = false
      }
    },
    typeLabelForKey (key) {
      if (key === 'mmm') return this.$t('views.adminTools.typeAdHoc')
      if (key === 'automatic') return this.$t('views.adminTools.typeDiy')
      if (key === 'ekrm') return this.$t('views.adminTools.typeCustomEkrm')
      return this.$t('views.adminTools.typeCustomReference')
    },
    formatDate (iso) {
      if (!iso) return '-'
      const d = new Date(iso)
      if (isNaN(d.getTime())) return '-'
      return d.toISOString().slice(0, 10)
    },
    sleep (ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    showSnack (text, color) {
      this.snackText = text
      this.snackColor = color || 'success'
      this.snack = true
    },
    async runPreview () {
      this.previewing = true
      this.previewDone = false
      this.matches = []
      this.undetermined = []
      this.selectedMatches = []
      try {
        const result = await this.previewBulkRecalculateModelReports({
          afterDate: this.afterDate,
          beforeDate: this.beforeDate,
          types: this.selectedTypes
        })
        this.matches = result.matches || []
        this.undetermined = result.undetermined || []
        // Default to all matched models selected.
        this.selectedMatches = this.matches.slice()
        this.previewDone = true
      } catch (error) {
        console.error('Bulk recalculation preview failed', error)
        this.showSnack(this.$t('views.adminTools.errorMessage'), 'error')
      } finally {
        this.previewing = false
      }
    },
    async runRecalculation () {
      const ids = this.effectiveSelectedIds
      if (ids.length === 0) {
        this.showSnack(this.$t('views.adminTools.selectAtLeastOne'), 'error')
        return
      }
      const size = this.batchSize && this.batchSize > 0 ? this.batchSize : 1
      this.recalculating = true
      this.progressTotal = ids.length
      this.progressDone = 0
      try {
        for (let i = 0; i < ids.length; i += size) {
          const batch = ids.slice(i, i + size)
          await this.bulkRecalculateModelReports({ ids: batch })
          await this.waitForBatch(batch)
          this.progressDone = Math.min(this.progressDone + batch.length, this.progressTotal)
        }
        this.showSnack(this.$t('views.adminTools.doneMessage', { count: ids.length }), 'success')
        // Refresh the preview so completed models drop out of range / show fresh dates.
        await this.runPreview()
      } catch (error) {
        console.error('Bulk recalculation failed', error)
        this.showSnack(this.$t('views.adminTools.errorMessage'), 'error')
      } finally {
        this.recalculating = false
      }
    },
    // Poll the recalculating flag for the batch until every model has finished (flag cleared) or
    // the per-batch timeout elapses, so we never spin up the next batch's VMs while these run.
    async waitForBatch (batch) {
      const deadline = Date.now() + BATCH_TIMEOUT_MS
      // Give the backend a moment to set the flags before the first poll.
      await this.sleep(POLL_INTERVAL_MS)
      while (Date.now() < deadline) {
        const status = await this.getRecalculatingStatus(batch)
        const stillRunning = batch.some(id => status[id])
        if (!stillRunning) {
          return
        }
        await this.sleep(POLL_INTERVAL_MS)
      }
    }
  }
}
